import { Fragment, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CirclePlusIcon, LinkIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useQuery } from '@apollo/client/react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem, SelectSeparator } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Form, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { AppsDocument } from 'src/graphql/actions/apps/apps.query';
import { getApiError } from 'src/utils/errors';

import { linkApplicationSchema, type ILinkApplicationForm } from '../model';

import type { IAppsQuery } from 'src/graphql/actions/apps/apps.query';

const APPS_PER_PAGE = 100;
const NEW_APPLICATION_VALUE = 'new-app';

type ILinkableApplication = IAppsQuery['apps']['nodes'][number];

type Props = {
  title: React.ReactNode;
  description: React.ReactNode;
  submitLabel: string;
  successMessage: string | ((application: ILinkableApplication) => string);
  onSubmit: (applicationId: string) => Promise<void>;
  onSuccess?: (applicationId: string) => void;
  triggerNode?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  excludeAppId?: string;
  onCreateNew?: () => void;
};

export const LinkApplicationDialog = ({
  title,
  description,
  submitLabel,
  successMessage,
  onSubmit,
  onSuccess,
  triggerNode,
  open: controlledOpen,
  onOpenChange,
  excludeAppId,
  onCreateNew,
}: Props) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;

  const { data } = useQuery(AppsDocument, { variables: { perPage: APPS_PER_PAGE, owned: true }, skip: !open });
  const applications = (data?.apps.nodes ?? []).filter((application) => application.id !== excludeAppId);

  const form = useForm<ILinkApplicationForm>({
    resolver: zodResolver(linkApplicationSchema),
    defaultValues: { applicationId: '' },
  });

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) form.reset();
    if (!isControlled) setUncontrolledOpen(nextOpen);
    onOpenChange?.(nextOpen);
  };

  const handleFormSubmit = async ({ applicationId }: ILinkApplicationForm) => {
    try {
      await onSubmit(applicationId);
      const application = applications.find(({ id }) => id === applicationId) as ILinkableApplication;
      toast.success(typeof successMessage === 'function' ? successMessage(application) : successMessage);
      handleOpenChange(false);
      onSuccess?.(applicationId);
    } catch (error) {
      getApiError(error, toast.error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {triggerNode && <DialogTrigger asChild>{triggerNode}</DialogTrigger>}
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleFormSubmit)}>
            <FormField
              control={form.control}
              name="applicationId"
              render={({ field, fieldState }) => {
                const handleApplicationChange = (value: string) => {
                  if (value === NEW_APPLICATION_VALUE) {
                    onCreateNew?.();
                    return;
                  }

                  field.onChange(value);
                };

                return (
                  <FormItem className="mb-6">
                    <FormLabel>Application</FormLabel>
                    <Select value={field.value} onValueChange={handleApplicationChange}>
                      <SelectTrigger aria-invalid={fieldState.invalid} className="w-full">
                        <SelectValue placeholder="Select an application" />
                      </SelectTrigger>
                      <SelectContent>
                        {applications.map((application) => (
                          <SelectItem key={application.id} value={application.id}>
                            {application.name ?? 'Untitled application'}
                          </SelectItem>
                        ))}
                        {onCreateNew && (
                          <Fragment>
                            <SelectSeparator />
                            <SelectItem value={NEW_APPLICATION_VALUE} className="text-sky-700 font-semibold">
                              <CirclePlusIcon className="size-3.5 text-sky-700" /> Create a new application
                            </SelectItem>
                          </Fragment>
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline" type="button">
                  Cancel
                </Button>
              </DialogClose>
              <Button type="submit" disabled={form.formState.isSubmitting} isLoading={form.formState.isSubmitting}>
                <LinkIcon /> {submitLabel}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
